import { Menu, Sun, Moon, Bell, ChevronDown, Headphones } from 'lucide-react'
import { GlobalSearch } from './GlobalSearch'
import { cn }              from '@/lib/utils'
import { useUIStore }      from '@/stores/ui.store'
import { useTheme }        from '@/hooks/useTheme'
import { useAuthStore }    from '@/stores/auth.store'
import { supabase }        from '@/lib/supabase'
import { LOGO_PROCREDITO } from '@/assets/logo'
import { useState }        from 'react'
import { useLocation }     from 'react-router-dom'
import { NAV_SECTIONS }    from '@/lib/navigation'

function getPageTitle(pathname: string): string {
  const all = NAV_SECTIONS.flatMap(s => s.modules)
  const match = all.find(m => m.path === pathname || (m.path !== '/' && pathname.startsWith(m.path)))
  return match?.label ?? 'Dashboard'
}

export function Topbar() {
  const collapsed           = useUIStore(s => s.sidebarCollapsed)
  const toggleMobileSidebar = useUIStore(s => s.toggleMobileSidebar)
  const { resolvedTheme, toggleTheme } = useTheme()
  const profile             = useAuthStore(s => s.profile)
  const authLoading         = useAuthStore(s => s.loading)
  const [menuOpen, setMenuOpen] = useState(false)
  const location  = useLocation()
  const pageTitle = getPageTitle(location.pathname)

  const handleSignOut = async () => {
    setMenuOpen(false)
    useAuthStore.getState().reset()
    await supabase.auth.signOut().catch(() => {})
    window.location.replace('/login')
  }

  const openWorkspace = () => {
    // Intenta abrir la app de escritorio instalada (procredito-callcenter://).
    // Si el sistema no tiene un manejador registrado para ese esquema, no
    // pasa nada visible y caemos al comportamiento de siempre (pestaña del
    // navegador) tras un breve margen.
    let handedOff = false
    const onHide = () => { handedOff = true }
    document.addEventListener('visibilitychange', onHide)
    window.addEventListener('blur', onHide)

    const cleanup = () => {
      document.removeEventListener('visibilitychange', onHide)
      window.removeEventListener('blur', onHide)
    }

    window.location.href = 'procredito-callcenter://open'

    window.setTimeout(() => {
      cleanup()
      if (!handedOff) {
        window.open('/workspace', 'cc-workspace', 'noopener,noreferrer')
      }
    }, 900)
  }

  const initial     = authLoading ? '·' : (profile?.full_name?.[0] ?? profile?.username?.[0] ?? 'U').toUpperCase()
  const displayName = authLoading ? 'Cargando...' : (profile?.full_name ?? profile?.username ?? 'Usuario')

  return (
    <header className={cn(
      'fixed top-0 right-0 z-20 flex h-[56px] items-center gap-2 sm:gap-3 px-3 sm:px-4',
      'border-b border-[var(--border)] bg-[var(--topbar-bg)]',
      'transition-[left] duration-200 ease-out left-0',
      collapsed ? 'md:left-[56px]' : 'md:left-[230px]',
    )}>
      <button
        type="button"
        onClick={toggleMobileSidebar}
        className="md:hidden flex items-center justify-center h-8 w-8 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--nav-hover-bg)] transition-colors shrink-0"
        aria-label="Abrir menú"
      >
        <Menu size={17} strokeWidth={1.8} />
      </button>

      <div className="md:hidden flex items-center gap-2 min-w-0 shrink">
        <img src={LOGO_PROCREDITO} alt="ProCredito" className="h-6 w-6 object-contain shrink-0" />
        <span className="text-sm font-bold text-[var(--text-primary)] tracking-tight truncate hidden xs:inline sm:inline">
          PROCREDITO
        </span>
      </div>

      <div className="flex items-center shrink-0 min-w-0">
        <GlobalSearch />
      </div>

      <div className="hidden lg:flex items-center gap-2 ml-1 min-w-0">
        <div className="h-3 w-px bg-[var(--border)] shrink-0" />
        <span className="text-xs font-medium text-[var(--text-muted)] truncate">{pageTitle}</span>
      </div>

      <div className="flex items-center gap-0.5 sm:gap-1 ml-auto shrink-0">
        {/* LIVE — sin botón Workspace aquí (solo menú usuario + Call Center) */}
        <div className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-lg border border-emerald-500/20 bg-emerald-500/5 mr-0.5">
          <div className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-[9px] font-mono font-bold text-emerald-400/90 tracking-wide">LIVE</span>
        </div>

        <button
          type="button"
          onClick={toggleTheme}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--nav-hover-bg)] transition-colors"
          title={resolvedTheme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
        >
          {resolvedTheme === 'dark'
            ? <Sun size={14} strokeWidth={1.8} />
            : <Moon size={14} strokeWidth={1.8} />
          }
        </button>

        <button
          type="button"
          className="relative hidden sm:flex h-8 w-8 items-center justify-center rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--nav-hover-bg)] transition-colors"
          aria-label="Notificaciones"
        >
          <Bell size={14} strokeWidth={1.8} />
        </button>

        <div className="mx-1 h-5 w-px bg-[var(--border)] hidden sm:block" />

        <div className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen(o => !o)}
            className="flex items-center gap-2 rounded-lg px-1.5 sm:px-2.5 py-1.5 hover:bg-[var(--nav-hover-bg)] transition-colors"
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-500/15 text-brand-400 font-bold text-xs border border-brand-500/20 shrink-0">
              {initial}
            </div>
            <div className="hidden md:block text-left min-w-0">
              <p className="text-xs font-semibold text-[var(--text-primary)] leading-none max-w-[100px] truncate">
                {displayName}
              </p>
              <div className="flex items-center gap-1 mt-0.5">
                {profile?.role === 'admin' && (
                  <span className="text-[9px] font-bold text-[#5582ff] uppercase tracking-wide">Admin</span>
                )}
                {profile?.role === 'supervisor' && (
                  <span className="text-[9px] font-bold text-[#f59e0b] uppercase tracking-wide">Supervisor</span>
                )}
                {profile?.role === 'agente' && (
                  <span className="text-[9px] text-[var(--text-muted)] capitalize leading-none">Agente</span>
                )}
                {!profile?.role && (
                  <span className="text-[9px] text-[var(--text-muted)] leading-none">—</span>
                )}
              </div>
            </div>
            <ChevronDown
              size={11}
              strokeWidth={2.5}
              className={cn(
                'hidden md:block text-[var(--text-muted)] transition-transform duration-150 shrink-0',
                menuOpen && 'rotate-180'
              )}
            />
          </button>

          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} aria-hidden="true" />
              <div className="absolute right-0 top-full mt-2 z-20 w-56 max-w-[calc(100vw-1.5rem)] rounded-xl border border-[var(--border)] bg-[var(--popover-bg)] shadow-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-[var(--border)] bg-[var(--bg-surface-2)]/30">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-500/15 text-brand-400 font-bold text-sm border border-brand-500/20 shrink-0">
                      {initial}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-[var(--text-primary)] truncate max-w-[140px]">{displayName}</p>
                      <div className="mt-0.5">
                        {profile?.role === 'admin' && <span className="text-[9px] font-bold text-[#5582ff] uppercase tracking-wide">Administrador</span>}
                        {profile?.role === 'supervisor' && <span className="text-[9px] font-bold text-[#f59e0b] uppercase tracking-wide">Supervisor</span>}
                        {profile?.role === 'agente' && <span className="text-[9px] text-[var(--text-muted)]">Agente</span>}
                        {!profile?.role && <span className="text-[9px] text-[var(--text-muted)]">Cargando...</span>}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-1.5">
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); openWorkspace() }}
                    className="w-full text-left flex items-center gap-2 px-3 py-2 text-xs text-[var(--text-primary)] rounded-lg hover:bg-[var(--nav-hover-bg)] font-medium transition-colors"
                  >
                    <Headphones size={13} className="text-brand-400" /> Workspace Call Center
                  </button>
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="w-full text-left flex items-center gap-2 px-3 py-2 text-xs text-red-400 rounded-lg hover:bg-red-400/10 font-medium transition-colors"
                  >
                    Cerrar sesión
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  )
}
